import React from "react";
import { Col, notification, Row, Table } from "antd";
import moment from "moment";
import { Component } from "react";
import { connect } from "react-redux";
import { delayedDispatch, setBreadcrumb, setLoader, setTitle, updateCrumb } from "store/actions";
import { API, Endpoints } from "utils/api";
import strings from "utils/strings";
import { Helmet } from "react-helmet";
import { ContentWrapper, Icon } from "components";
import { formatPrice, getSuccessLevelBadge, xlsxSettings } from "utils/utils";
import { Area } from "@ant-design/charts";
import { getColumnFilter } from "utils/comp-utils";
import { DateTime } from "luxon";
import xlsx from "json-as-xlsx";

class Diamond extends Component<any, any> {
	searchTimeout: NodeJS.Timeout | undefined;

	constructor(props: any) {
		super(props);

		this.state = {
			startDate: moment().add({ months: -5 }).startOf("month"),
			endDate: moment().endOf("month"),
			partnerDetail: null,
			eligible: false,
			commissionsChart: [],
			commissions: []
		};
	}

	async componentDidMount(): Promise<void> {
		const { dispatch } = this.props;

		dispatch(setTitle(""));

		this.breadcrumb();
		await this.getData();
	}

	componentDidUpdate(): void {
		const { dispatch } = this.props;
		dispatch(updateCrumb());
	}

	handleSelect = (dates: any) => {
		const newDates = [];
		if (dates && dates[0]) {
			const start = moment(dates[0]);
			newDates.push(start.startOf("month"));
		}

		if (dates && dates[1]) {
			const end = moment(dates[1]);
			newDates.push(end.endOf("month"));
		}

		if (newDates[0] && newDates[1] && newDates[0].isSame(newDates[1], "month")) {
			newDates[0] = moment(newDates[0]).startOf("month");
			newDates[1] = moment(newDates[1]).endOf("month");
		}

		this.setState(
			{
				startDate: newDates[0],
				endDate: newDates[1]
			},
			() => this.getData()
		);
	};

	async getData() {
		const { startDate, endDate } = this.state;
		const { dispatch, countries } = this.props;

		dispatch(setLoader(true));

		const body = {
			type: "diamond",
			startDate: startDate.toISOString(),
			endDate: endDate.toISOString()
		};

		try {
			const response = await API.post({
				url: Endpoints.uriCommissions("search-by-type"),
				data: body
			});

			if (response.ok) {
				const { partner, eligible = false, settings, commissionsChart, commissions, cards } = response.data.results;

				const commissionsParsed = commissions.map((entry: any) => {
					const countryCallingCodePartner = entry.partner?.phone?.substring(0, 4).replace("+", "");
					let countryPartner;

					if (countryCallingCodePartner) {
						const countryObj = countries.find((elem: any) => elem.callingCodes.includes(countryCallingCodePartner));
						countryPartner = {
							name: countryObj?.name,
							alpha2Code: countryObj?.alpha2Code
						};
					}

					return {
						...entry,
						partner: { ...entry.partner, country: countryPartner }
					};
				});

				this.setState({
					partnerDetail: partner,
					eligible,
					settings,
					commissionsChart,
					commissions: commissionsParsed,
					cards
				});
			} else {
				notification.error({
					message: strings.commissions.special,
					description: response?.data?.message || strings.serverErrors.wentWrong,
					placement: "bottomRight",
					duration: 5
				});
			}
		} catch (err) {
			notification.error({
				message: strings.serverErrors.title,
				description: err as string,
				placement: "bottomRight",
				duration: 5
			});
		}

		dispatch(setLoader(false));
	}

	breadcrumb() {
		delayedDispatch(
			setBreadcrumb(() => {
				const { startDate, endDate } = this.state;

				return {
					locations: [
						{
							text: strings.commissions.diamond,
							icon: "organization"
						}
					],
					actions: [
						{
							type: "datePicker",
							text: strings.dashboard.period,
							dates: [startDate, endDate],
							monthly: true,
							onChange: this.handleSelect,
							className: "fixedPicker"
						}
					]
				};
			})
		);
	}

	//TODO: Export with the right fields
	exportData = () => {
		const { data: exportData = [], partner, partnerOptions } = this.state;

		const columns = [
			{ label: "Data", value: (row: any) => DateTime.fromISO(row.date).toFormat("dd/MM/yyyy HH:mm") },
			{
				label: "Parceiro",
				value: (row: any) => `${row.partner.name} (#${row.partner.userCode})`
			},
			{
				label: "Nível de Sucesso",
				value: (row: any) => getSuccessLevelBadge(row.actualSuccessLevel) || "-"
			},
			{ label: "Valor da Encomenda", value: (row: any) => (row.order ? formatPrice(row.order.total) : "-") },
			{ label: "Valor da Comissão", value: (row: any) => formatPrice(row.value) || "-" },
			{
				label: "Data de Expiração",
				value: (row: any) => (row.expirationDate ? DateTime.fromISO(row.expirationDate).toFormat("dd/MM/yyyy HH:mm") : "-")
			}
		];

		const data = [
			{
				sheet: "Bónus de Repasse",
				columns,
				content: exportData
			}
		];

		let name = "Bónus de Repasse";
		if (partner) {
			const partnerData = partnerOptions.find((elem: any) => elem._id === partner);
			if (partnerData) name = `Bonus de Repasse - ${partnerData.name}`;
		}

		xlsx(data, xlsxSettings(name));
	};

	renderMovements() {
		const { commissions = [] } = this.state;

		return (
			<ContentWrapper>
				<div className="ScreenHeader --multi">
					<div className="ScreenHeaderLeft">
						<Icon name="diagram" />
						<h2>{strings.commissions.diamond}</h2>
					</div>
					<div className="ScreenHeaderRight">
						<p>
							{strings.formatString(
								strings.wallet.earnedPeriod,
								formatPrice(commissions.reduce((acc: any, entry: any) => acc + entry.value, 0))
							)}
						</p>
						<button className="EthicAddButton" onClick={this.exportData}>
							<Icon name="print" />
						</button>
					</div>
				</div>
				<Table
					style={{ marginTop: 20 }}
					scroll={{ x: "max-content" }}
					columns={[
						{
							title: strings.fields.date,
							dataIndex: "date",
							key: "date",
							render: (date: string) => DateTime.fromISO(date).toFormat("dd/MM/yyyy HH:mm"),
							defaultSortOrder: "descend",
							sorter: (a, b) => DateTime.fromISO(a.date).toMillis() - DateTime.fromISO(b.date).toMillis(),
							width: 150
						},
						{
							title: strings.commissions.value,
							dataIndex: "value",
							key: "value",
							render: (value: number) => formatPrice(value) || "-",
							sorter: (a, b) => a.value - b.value,
							width: 150
						},
						{
							title: strings.bonuses.bonusRecruitmentHeader2,
							dataIndex: "points",
							key: "points",
							sorter: (a, b) => a.points - b.points,
							width: 150
						},
						{
							title: strings.generic.expirationDate,
							dataIndex: "expirationDate",
							key: "expirationDate",
							render: (value: string) => (value ? DateTime.fromISO(value).toFormat("dd/MM/yyyy HH:mm") : "-"),
							align: "center",
							sorter: (a, b) => DateTime.fromISO(a._created).toMillis() - DateTime.fromISO(b._created).toMillis(),
							width: 150
						}
					]}
					dataSource={commissions}
					rowKey={(record) => record._id}
				/>
			</ContentWrapper>
		);
	}

	renderChart() {
		const { commissionsChart = [] } = this.state;

		const config = {
			data: commissionsChart,
			xField: "month",
			yField: "diamond",
			appendPadding: 10,
			smooth: true,
			animation: {
				appear: {
					animation: "path-in",
					duration: 0
				}
			},
			meta: {
				diamond: {
					alias: strings.wallet.totalBilled
				}
			},
			tooltip: {
				formatter: (datum: any) => {
					return { name: strings.wallet.totalBilled, value: formatPrice(datum.diamond) };
				}
			}
		};

		return (
			<ContentWrapper extraStyle={{ marginBottom: 0 }}>
				<div className="ScreenHeader">
					<div className="ScreenHeaderLeft">
						<Icon name="diagram" />
						<h2>{strings.wallet.monthlyControl}</h2>
					</div>
				</div>
				<Area style={{ marginTop: 20 }} {...config} />
			</ContentWrapper>
		);
	}

	render() {
		const { partner, cards, eligible, partnerDetail } = this.state;
		const { available = 0, spent = 0, expired = 0, total = 0 } = cards || {};

		const CARDS = [
			{
				icon: "wallet",
				value: total,
				description: partner ? strings.commissions.earnedFromRepass : strings.commissions.totalAmountEarned,
				type: "currency"
			},
			{
				icon: "wallet",
				value: available,
				description: strings.wallet.available,
				type: "currency"
			},
			{
				icon: "wallet",
				value: spent,
				description: strings.wallet.spent,
				type: "currency"
			},
			{
				icon: "wallet",
				value: expired,
				description: strings.wallet.expired,
				type: "currency"
			}
		];

		return (
			<div className="CommissionDiamond">
				<Helmet>
					<title>{strings.commissions.diamond}</title>
					<meta name="description" content="Description of diamond commissions" />
				</Helmet>

				{Boolean(partner) && !eligible && (
					<Col xs={24}>
						<ContentWrapper extraStyle={{ backgroundColor: "#dce0dd" }}>
							<div className="ScreenHeader">
								<div className="ScreenHeaderLeft">
									<Icon name="user" />
									<h2>{strings.commissions.eligibility}</h2>
								</div>
							</div>
							<p>
								<strong>
									{strings.commissions.actualRank}: {getSuccessLevelBadge(partnerDetail?.partner?.successLevel)}
								</strong>
							</p>
							<p className="CommissionsPartnersAttention">{strings.commissions.notEligibleBronze}</p>
						</ContentWrapper>
					</Col>
				)}

				<div className="IconCards">
					<Row gutter={[20, 20]}>
						{CARDS.map((card, index) => (
							<Col xs={24} md={6} key={index}>
								<div className="IconCardOuterContainer">
									<div className="IconCard">
										<div className="IconCardHeader">
											<div className="IconCardHeaderMask" />
											<div className="IconCardType">
												<Icon name={card.icon} />
											</div>
										</div>
										<div className="IconCardBody">
											<span className="IconCardValue">
												{card.type === "currency" ? formatPrice(card.value) : card.value}
											</span>
											<h3 className="IconCardDescription">{card.description}</h3>
										</div>
									</div>
								</div>
							</Col>
						))}
						<Col xs={24}>{this.renderChart()}</Col>
						<Col xs={24}>{this.renderMovements()}</Col>
					</Row>
				</div>
			</div>
		);
	}
}

const mapStateToProps = (state: any) => ({
	language: state.language,
	countries: state.countries
});

export default connect(mapStateToProps)(Diamond);
